import { ObjectsDrawer } from './ObjectsDrawer';
import { AspObject, ObjectType } from './AspObject';
import { DisplayObject } from './DisplayObject';
import { IMapMngr, IObjectsDrawer, IObjectsPool } from './interfaces';
import { ObjectsPool } from './ObjectsPool';
import * as units from '../common/units';
import * as utils from '../common/utils';
import * as settings from '../common/settings';
import * as consts from '../common/consts';
import { aspMngr } from './AspMngr';
import { GeoPosition, SphericalPosition } from '../coordinates/Coordinates';
import { AppInjector } from '../common/inject';
import { MsgBoxService } from '../msg-boxes/msg-box.service';
import { Consts } from '@core_n/coordinates/Consts';

export const RULER_COLOR = [1.00, 0.55, 0.00, 1]; // dark orange
export const RULER_COLOR_CSS = utils.newmiColorToString(RULER_COLOR);
export const RULER_SYMBOL = "ruler-icon";
export const RULER_SYMBOL_CSS = utils.getPlaneSymbol(RULER_SYMBOL);


const rulerLineWidth = 2;

const miscSymbolNames = [
  'circle-small',
  'arrow-small'
];

const circleLetter = 'f';
const arrowLetter = 't';

const imagesToLoad = ObjectsDrawer.initImagesToLoad(undefined, undefined, miscSymbolNames);

enum RulerDataType {
  RulerText = 'ruler-text',
  RulerEdge = 'ruler-edge'
}

//
// Inner classes
//

//
// Ruler object
//
class RulerObject extends AspObject {

  constructor(objectsPool: IObjectsPool, geometry, public fromObjId?, public toObjId?) {
    super(objectsPool, objectsPool.getObjectType(), AspObject.generateId(objectsPool.getObjectType()));
    this.geometry = geometry;
  }

  isValid(): boolean {
    if (this.fromObjId) {
      let obj = aspMngr.getObject(this.fromObjId);
      if (!obj || !obj.active())
        return false;
    }
    if (this.toObjId) {
      let obj = aspMngr.getObject(this.toObjId);
      if (!obj || !obj.active())
        return false;
    }
    return true;
  }

  reverse() {
    let tmpObjId = this.fromObjId;
    this.fromObjId = this.toObjId;
    this.toObjId = tmpObjId;
    this.geometry.reverse();
    this.notifyDataChange();
  }

  handleMsg(msgObj: any): boolean { return false; }

  getTitle(): string {
    if (!this.isValid())
      return ``;
    let from = this.fromObjId ? aspMngr.getObject(this.fromObjId).getTitle() : `point`;
    let to = this.toObjId ? aspMngr.getObject(this.toObjId).getTitle() : `point`;
    return `Ruler from ${from} to ${to}`;
  }

  // Override AspObject
  // color for newmi drawing
  getColor() {
    return RULER_COLOR;
  }

  // color for css html 
  getCssColor() {
    return RULER_COLOR_CSS;
  }

  // gets the svg icon name
  getSymbol() {
    return RULER_SYMBOL;
  }

  // gets full path and the icon
  getSvgSymbol() {
    return RULER_SYMBOL_CSS;
  }
}

//
// Rulers pool
//
export class RulersPool extends ObjectsPool {

  private static _self: RulersPool = null;

  public static getSelf(): RulersPool {
    if (this._self == null)
      this._self = new RulersPool();
    return this._self;
  }

  private constructor() {
    super(ObjectType.Ruler, consts.MSG_TYPE_NON_SUPPORT, undefined, false, true /* clickable */, false);
    aspMngr.addObjectsPool(this);
  }

  createRuler(rulerGeometry: any, fromObjId: any, toObjId: any) {
    const newRulerObj = new RulerObject(this, rulerGeometry, fromObjId, toObjId);
    this.addObject(newRulerObj);
  }

  contextMenuActions(obj: AspObject): number[] {
    return [consts.USER_ACTION_RULER_REMOVE, consts.USER_ACTION_RULER_REVERSE];
  }

  globalMenuActions(obj: AspObject): number[] {
    return [consts.USER_ACTION_RULER_REMOVE_ALL];
  }

  handleUserAction(action: number, obj: AspObject, mapMngr: IMapMngr): boolean {
    if (action == consts.USER_ACTION_RULER_REMOVE) {
      this.deleteObject(obj);
      return true;
    }
    else if (action == consts.USER_ACTION_RULER_REVERSE) {
      let rulerObj = obj as RulerObject;
      rulerObj.reverse();
      return true;
    }
    else if (action == consts.USER_ACTION_RULER_REMOVE_ALL) {
      this.confirmAndDeleteAllObjects();
      return true;
    }
    return false;
  }

  async confirmAndDeleteAllObjects() {
    let confirmed = await AppInjector.get(MsgBoxService).showConfirmMsgBox(consts.constsMngr.translations['ASK_RULER_REMOVE'], 
                                                                          consts.constsMngr.translations['REMOVE_RULER'], 
                                                                          consts.constsMngr.translations['YES'], 
                                                                          consts.constsMngr.translations['NO']);
    if (!confirmed)
      return;
    this.deleteAllObjects();
  }

  periodicUpdate(): boolean {
    let objsToDelete = [];
    this.allObjects.forEach((obj: AspObject) => {
      if (!obj.isValid())
        objsToDelete.push(obj);
    });
    objsToDelete.forEach((obj: AspObject) => {
      this.deleteObject(obj);
    });

    return true;
  }

  handleMsg(msgObj: any): void { }
}

//
// Ruler display object
//
export class RulerDisplayObject extends DisplayObject {

  private static showElevation = false;

  private fromX: number;
  private fromY: number;
  private toX: number;
  private toY: number;

  private displayUnits: settings.DisplayUnits;

  constructor(aspObject: AspObject, drawer: IObjectsDrawer) {
    super(aspObject, drawer);

    this.drawData[aspObject.objectType].line = { width: rulerLineWidth, color: RULER_COLOR };
  }

  static setShowElevation(show: boolean) {
    RulerDisplayObject.showElevation = show;
  }

  private setGeometry() {
    this.geometry = this.aspObject.geometry.clone();
    this.boundsRect = this.geometry.getEnvelope().getRect();
    this.drawData[this.aspObject.objectType].geometry = this.geometry;
  }

  private createTexts() {
    let geometry = this.geometry as NeWMI.Geometry.Base.AMultiPoint;
    if (geometry && geometry.points.length) {

      let totalDistance: number = 0;
      let angleSwitched: boolean;
      let segAngle: number;
      let lastSegAngle: number;
      let altitudeGap: number;
      let elevation: number;

      const types = [];
      const dd = [];

      let mapMngr = this.drawer.getMapMngr();
      let rulerObj = this.aspObject as RulerObject;
      let fromObjPoint, toObjPoint;

      if (rulerObj.fromObjId) {
        let fromObj = mapMngr.getObject(rulerObj.fromObjId);
        if (fromObj)
          fromObjPoint = fromObj.getPresentationPoint();
      }
      if (rulerObj.toObjId) {
        let toObj = mapMngr.getObject(rulerObj.toObjId);
        if (toObj)
          toObjPoint = toObj.getPresentationPoint();
      }
      if (fromObjPoint != undefined || toObjPoint != undefined) {
        if (RulerDisplayObject.showElevation) {
          let fromPos = fromObjPoint != undefined ? fromObjPoint : geometry.points[0];
          let toPos = toObjPoint != undefined ? toObjPoint : geometry.points[geometry.points.length - 1];
          let fromGeoPos: GeoPosition =
            new GeoPosition(fromPos.y * units.DEG_TO_RAD, fromPos.x * units.DEG_TO_RAD, fromPos.z ? fromPos.z : 0);
          let toGeoPos: GeoPosition =
            new GeoPosition(toPos.y * units.DEG_TO_RAD, toPos.x * units.DEG_TO_RAD, toPos.z ? toPos.z : 0);
          let sphPos: SphericalPosition = toGeoPos.toSpherical(fromGeoPos);
          elevation = sphPos.getElevation();
        }
        else {
          altitudeGap = (toObjPoint ? toObjPoint.z : 0) - (fromObjPoint ? fromObjPoint.z : 0);
        }
      }

      let objCurrPnt = geometry.points[0];

      for (let currPtIndex = 1; currPtIndex < geometry.points.length; currPtIndex++) {
        let objNextPt = geometry.points[currPtIndex];

        const segAzimuth = NeWMI.Service.Spatial.GeodeticSvc.getAzimuth(objCurrPnt.x, objCurrPnt.y, objNextPt.x, objNextPt.y);
        let segAzimuthDeg = NeWMI.Service.Math.ConversionsSvc.fixAngleDegrees(Math.round(segAzimuth));
        let segAzimuthStr: string;
        if (Number.isNaN(segAzimuthDeg))
          segAzimuthStr = '0';
        else
          segAzimuthStr = segAzimuthDeg.toString().padStart(3, '0');

        // And now the angle for drawing
        segAngle = NeWMI.Service.Math.MeasurementSvc.getTrigonometricAngle(objCurrPnt.x, objCurrPnt.y, objNextPt.x, objNextPt.y);
        lastSegAngle = segAngle;

        // Flipping the text so it will not be upside down between some angles
        if (segAngle > Math.PI / 2 && segAngle < Math.PI * 1.5) {
          segAngle += Math.PI;
          angleSwitched = true;
        } else {
          angleSwitched = false;
        }

        // calculation and setting current distances results...
        let segDistanceInMeters = NeWMI.Service.Math.MeasurementSvc.gcDistance(objCurrPnt.x, objCurrPnt.y, objNextPt.x, objNextPt.y);

        totalDistance += segDistanceInMeters;

        // drawing the current segment distance text only in case it is equal or greater than the minimum distance for displaying the distance...
        if (segDistanceInMeters > 0) {
          const currGeo = new NeWMI.Geometry.Polyline({ points: [objCurrPnt, objNextPt] });
          const currGeoPt = currGeo.getPresentationPoint();

          let rulerTextData = this.drawData[RulerDataType.RulerText + currPtIndex];
          if (!rulerTextData) {
            rulerTextData = new NeWMI.Object.NeWMIObjectDM.TextOrSymbolDrawData();

            this.drawData[RulerDataType.RulerText + currPtIndex] = rulerTextData;

            types.push(RulerDataType.RulerText);
            dd.push(RulerDataType.RulerText + currPtIndex);
          }

          rulerTextData.location = currGeoPt;
          if (RulerDisplayObject.showElevation) {
            rulerTextData.value = `${this.getDistanceText(segDistanceInMeters)} | ${segAzimuthStr}${units.DEGREE_CHAR}`;
            if (elevation != undefined)
              rulerTextData.value = `${rulerTextData.value} | ${this.getElevationText(elevation)}`;
          }
          else {
            rulerTextData.value = `${segAzimuthStr}${units.DEGREE_CHAR} | ${this.getDistanceText(segDistanceInMeters)}`;
            if (altitudeGap != undefined)
              rulerTextData.value = `${rulerTextData.value} | ${this.getAltitudeText(altitudeGap)}`;
          }
          rulerTextData.alignment = { horizontal: 'center', vertical: 'bottom' };
          rulerTextData.color = RULER_COLOR;

          let cosAng = Math.cos(-segAngle);
          let sinAng = Math.sin(-segAngle);
          rulerTextData.rotation = [cosAng, sinAng];

          cosAng = Math.cos(segAngle);
          sinAng = Math.sin(segAngle);
          let offestX = 0;
          let offsetY = -6;
          rulerTextData.offset = [offestX * cosAng + offsetY * sinAng, -offestX * sinAng + offsetY * cosAng];
        }

        objCurrPnt = objNextPt;
      }

      // // drawing the total distance only in case it is equal or greater than the minimum distance value
      // if (totalDistance > 0 && geometry.points.length > 2) {
      //   const rulerTextData = new NeWMI.Object.NeWMIObjectDM.TextOrSymbolDrawData();
      //   rulerTextData.location = geometry.points[geometry.points.length - 1];
      //   rulerTextData.value = `${this.getDistanceText(totalDistance)}`;
      //   rulerTextData.alignment = { horizontal: angleSwitched ? 'right' : 'left', vertical: 'middle' };
      //   rulerTextData.offset = [angleSwitched ? -6 : 6, 0];
      //   rulerTextData.color = rulerColor;

      //   this.drawData[RulerDataType.RulerText + geometry.points.length] = rulerTextData;

      //   if (addToDm) {
      //     types.push(RulerDataType.RulerText);
      //     dd.push(RulerDataType.RulerText + geometry.points.length);
      //   }
      // }

      let rulerEdgeData = this.drawData[RulerDataType.RulerEdge + 'from'];
      if (!rulerEdgeData) {
        rulerEdgeData = new NeWMI.Object.NeWMIObjectDM.TextOrSymbolDrawData();

        this.drawData[RulerDataType.RulerEdge + 'from'] = rulerEdgeData;

        types.push(RulerDataType.RulerEdge);
        dd.push(RulerDataType.RulerEdge + 'from');
      }

      rulerEdgeData.location = geometry.points[0];
      rulerEdgeData.alignment = { horizontal: 'center', vertical: 'middle' };
      rulerEdgeData.value = circleLetter;
      rulerEdgeData.color = RULER_COLOR;

      rulerEdgeData = this.drawData[RulerDataType.RulerEdge + 'to'];
      if (!rulerEdgeData) {
        rulerEdgeData = new NeWMI.Object.NeWMIObjectDM.TextOrSymbolDrawData();

        this.drawData[RulerDataType.RulerEdge + 'to'] = rulerEdgeData;

        types.push(RulerDataType.RulerEdge);
        dd.push(RulerDataType.RulerEdge + 'to');
      }

      rulerEdgeData.location = geometry.points[geometry.points.length - 1];
      rulerEdgeData.alignment = { horizontal: 'center', vertical: 'middle' };
      rulerEdgeData.value = arrowLetter;
      rulerEdgeData.color = RULER_COLOR;

      let arrowAngle = -lastSegAngle + Math.PI / 2;
      if (arrowAngle > Math.PI)
        arrowAngle -= Math.PI * 2;
      else if (arrowAngle < -Math.PI)
        arrowAngle += Math.PI * 2;
      let cosAng = Math.cos(arrowAngle);
      let sinAng = Math.sin(arrowAngle);
      rulerEdgeData.rotation = [cosAng, sinAng];

      if (dd.length > 0)
        this.drawer.addObjectToDm(this, dd, types);
    }
  }

  protected prepareToDraw() {
    let needToUpdate = false;
    let mapMngr = this.drawer.getMapMngr();
    let rulerObj = this.aspObject as RulerObject;
    let geometry = this.geometry as NeWMI.Geometry.Base.AMultiPoint;

    if (rulerObj.fromObjId) {
      let fromObj = mapMngr.getObject(rulerObj.fromObjId);
      if (!fromObj || !fromObj.isVisible()) {
        aspMngr.deleteObject(rulerObj.id);
        return;
      }
      else {
        let fromObjLoc = fromObj.getPresentationPoint();
        if (this.fromX != fromObjLoc.x || this.fromY != fromObjLoc.y) {
          needToUpdate = true;
          this.fromX = fromObjLoc.x;
          this.fromY = fromObjLoc.y;
          geometry.points[0].x = this.fromX;
          geometry.points[0].y = this.fromY;
          geometry.points[0].z = fromObjLoc.z;
        }
      }
    }

    if (rulerObj.toObjId) {
      let toObj = mapMngr.getObject(rulerObj.toObjId);
      if (!toObj || !toObj.isVisible()) {
        aspMngr.deleteObject(rulerObj.id);
        return;
      }
      else {
        let toObjLoc = toObj.getPresentationPoint();
        if (this.toX != toObjLoc.x || this.toY != toObjLoc.y) {
          needToUpdate = true;
          this.toX = toObjLoc.x;
          this.toY = toObjLoc.y;
          geometry.points[geometry.points.length - 1].x = this.toX;
          geometry.points[geometry.points.length - 1].y = this.toY;
          geometry.points[geometry.points.length - 1].z = toObjLoc.z;
        }
      }
    }

    if (this.displayUnits != settings.userSettings.displayUnits) {
      this.displayUnits = settings.userSettings.displayUnits;
      needToUpdate = true;
    }

    if (needToUpdate) {
      this.boundsRect = this.geometry.getEnvelope().getRect();
      this.createTexts();
      if (this.isDisplayed)
        this.drawer.updateObjectInDm(this);
    }
  }

  onDataChange(): void {
    this.setGeometry();
    this.prepareToDraw();
    this.createTexts();

    if (this.isDisplayed) {
      this.drawer.updateObjectInDm(this);
    }
    else {
      this.drawer.addObjectToDm(this);
    }

    this.drawer.getMapMngr().redrawMap();
  }

  private getDistanceText(distInMeters) {
    let dist = units.distanceValue(distInMeters, 1);
    let label = units.distanceUnits();
    return `${dist}${label.toLowerCase()}`;
  }

  private getAltitudeText(altitude) {
    let alt = Math.round(altitude * units.METER_TO_FEET);
    let label = units.FT_LABEL.toLowerCase();
    if (alt >= 10000) {
      alt = Math.round(alt / 100);
      label = units.FT_LABEL.toLowerCase();
    }
    return `${alt}${label}`;
  }

  private getElevationText(elevation) {
    let elev = (elevation * units.RAD_TO_DEG).toFixed(1);
    return `${elev}${units.DEGREE_CHAR}`;
  }
}

//
// Rulers drawer
//
export class RulersDrawer extends ObjectsDrawer {

  constructor(mapMngr: IMapMngr, parentLayer) {
    super(mapMngr, RulersPool.getSelf(), null, parentLayer, null, imagesToLoad, null, null);

    this.dataKeys.push(RulerDataType.RulerText);
    this.typeKeys.push(RulerDataType.RulerText);
  }

  protected addDrawTypes(images) {
    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.Geometry, this.objectsType,
      {
        autoIndex: { enabled: true, selectable: true },
        line: { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor }
      });

    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.TextOrSymbol, RulerDataType.RulerText,
      {
        style: this.normalTextStyle,
        properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EORotation | NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor,
      });

    let arrImages = [];
    arrImages[circleLetter] = images['circle-small'];
    arrImages[arrowLetter] = images['arrow-small'];
    let symbolStyle = new NeWMI.Draw.Styles.ImagesFontStyle({ images: arrImages, isSymbol: true });

    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.TextOrSymbol, RulerDataType.RulerEdge,
      {
        style: symbolStyle,
        properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EORotation | NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor,
      });
  }

  createDisplayObject(obj: AspObject): DisplayObject {
    const newRuler = new RulerDisplayObject(obj, this);
    return newRuler;
  }

  onObjectDelete(obj: AspObject): void {
    // notified of ruler object deletion
    super.onObjectDelete(obj);
    this.mapMngr.redrawMap();
  }

}
